import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_BASE_URL } from '../../tokens/api.tokens';

const OLD_MEDIA_HOST_REGEX =
  /https?:\/\/(?:gulu-media-[a-z0-9]+\.[a-z0-9]+\.azurefd\.net|stguluprodm960571\.blob\.core\.windows\.net)/gi;
const CANONICAL_MEDIA_HOST = 'https://media.gulu.ae';

function normalizeMediaString(val: string): string {
  if (val.includes('azurefd.net') || val.includes('stguluprodm960571.blob.core.windows.net')) {
    return val.replace(OLD_MEDIA_HOST_REGEX, CANONICAL_MEDIA_HOST);
  }
  return val;
}

function normalizeMediaResponseBody<T>(body: T): T {
  if (!body || typeof body !== 'object') return body;

  try {
    const stack: unknown[] = [body];
    let visitedCount = 0;

    // Fast iterative object graph walk — eliminates JSON.stringify/JSON.parse main-thread freeze
    while (stack.length > 0 && visitedCount < 10000) {
      visitedCount++;
      const current = stack.pop();
      if (!current || typeof current !== 'object') continue;

      if (Array.isArray(current)) {
        for (let i = 0; i < current.length; i++) {
          const item = current[i];
          if (typeof item === 'string') {
            current[i] = normalizeMediaString(item);
          } else if (item && typeof item === 'object') {
            stack.push(item);
          }
        }
      } else {
        const obj = current as Record<string, unknown>;
        for (const key of Object.keys(obj)) {
          // Direct Azure Blob SAS uploads MUST go directly to stguluprodm960571.blob.core.windows.net
          // Cloudflare's 100MB body size limit kills large video uploads with HTTP 413 if rewritten.
          if (key === 'uploadUrl') {
            continue;
          }
          const val = obj[key];
          if (typeof val === 'string') {
            obj[key] = normalizeMediaString(val);
          } else if (val && typeof val === 'object') {
            stack.push(val);
          }
        }
      }
    }

    return body;
  } catch {
    return body;
  }
}

export const baseUrlInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const baseUrl = inject(API_BASE_URL);

  let apiReq = req;
  if (req.url.startsWith('/api') && !req.url.startsWith(baseUrl)) {
    const url = req.url.replace('/api', baseUrl);
    apiReq = req.clone({ url });
  }

  return next(apiReq).pipe(
    map((event) => {
      if (event instanceof HttpResponse && event.body) {
        const transformedBody = normalizeMediaResponseBody(event.body);
        if (transformedBody !== event.body) {
          return event.clone({ body: transformedBody });
        }
      }
      return event;
    }),
  );
};
